import { useEffect, useRef, useMemo } from 'react';
import useComponent from 'hooks/useComponent.hook';
import ConversationsHeader from 'views/chat/components/headers/ConversationsHeader.component';
import ContactConversationsList from 'views/chat/components/lists/ContactConversationsList.component.js';
import NeoSlideMenu from 'design/design_components/neo/menu/NeoSlideMenu.base';
import NeoButtonMain from 'design/design_components/neo/button/NeoButtonMain.base';
import useAuthentication from 'hooks/useAuthentication.hook';
import useChat from 'hooks/chat/useChat.hook';
import { getUnattendedLabel } from 'utils/conversations.util';
import { useGlobalTimer } from 'hooks/useGlobalTimer.hook';

const FILTER_STATUS = {
  ALL: 1,
  NEW_MESSAGES: 2,
  TIME_ELAPSED: 3,
  FINISHED: 4,
  REASSIGNED: 5
};

const FILTER_ORDER = {
  LONGER_WAITING_TIME: 1,
  MORE_RECENT: 2
};

export default function ConversationsSection(props) {
  const chat = useChat();
  const tick = useGlobalTimer();
  const authentication = useAuthentication();
  const component = useComponent({
    state: {
      transition: '',
      filterStatusActive: Object.values(FILTER_STATUS),
      filterOrderActive: FILTER_ORDER.LONGER_WAITING_TIME
    }
  });

  useEffect(() => {
    setTimeout(() => {
      component.setState((state) => {
        state.transition = "transition";
      })
    }, 1)

    return () => {
      chat.dispatch((TYPE) => {
        return {
          type: TYPE.CONVERSATION.SELECTED.TYPE,
          payload: null
        }
      })
    }
  }, []);

  const conversationsWithFlags = useMemo(() => {
    return props.conversations.map(conversation => ({
      ...conversation,
      unattendedLabel: getUnattendedLabel(conversation),
    }));
  }, [tick, props.conversations]);

  const orderConversationsByUserWaiting = (conversations) => {
    const newArrayOfConversations = [];

    const conversationsWithMessageUser  = orderConversationsByRecentAsc(conversations.filter((conversation) => {
      if(!conversation?.lastMessageWasSentByContact) {
        newArrayOfConversations.push(conversation);
      }
      return conversation?.lastMessageWasSentByContact;
    })).reverse();

    newArrayOfConversations.unshift(...conversationsWithMessageUser);
    return newArrayOfConversations;
  }

  const orderConversationsByRecentAsc = (conversations) => {
    return [...conversations].sort((conversationA, conversationB) => {
      return new Date(conversationB.lastMessageSentAt) - new Date(conversationA.lastMessageSentAt);
    });
  }

  const applyFilters = (conversations = [], filters = [], order = FILTER_ORDER.LONGER_WAITING_TIME) => {
    // Filter by status
    conversations = conversations.filter((conversation) => {
      let add = filters.includes(FILTER_STATUS.ALL);

      if(!add && filters.includes(FILTER_STATUS.NEW_MESSAGES)) {
        add = Boolean(conversation?.newMessages);
      }

      if (!add && filters.includes(FILTER_STATUS.TIME_ELAPSED)) {
        add = Boolean(conversation?.unattendedLabel);
      }

      if(!add && filters.includes(FILTER_STATUS.FINISHED)) {
        add = Boolean(conversation?.isFinished);
      }

      if(!add && filters.includes(FILTER_STATUS.REASSIGNED)) {
        add = Boolean(conversation?.reassigned);
      }

      return add;
    });

    // Order
    switch (order) {
      case FILTER_ORDER.LONGER_WAITING_TIME:
        conversations = orderConversationsByUserWaiting(conversations);
        break;
      case FILTER_ORDER.MORE_RECENT:
        conversations = orderConversationsByRecentAsc(conversations);
        break;
      default:
        break;
    }

    return conversations;
  }

  const filteredConversations = useMemo(() => 
    applyFilters(conversationsWithFlags, component.state.filterStatusActive, component.state.filterOrderActive)
  , [conversationsWithFlags, component.state.filterStatusActive, component.state.filterOrderActive]);

  const FilterStatus = () => {
    const menu = useRef(null);
    const filterStatusesArray = (status) => {
      if(component.state.filterStatusActive.includes(status)) {
        component.setState((state) => {
          state.filterStatusActive = state.filterStatusActive.filter(filter => filter !== status && filter !== FILTER_STATUS.ALL);
        })
      } else {
        component.setState((state) => {
          state.filterStatusActive = state.filterStatusActive.filter(filter => filter !== FILTER_STATUS.ALL).concat([status]);
        })
      }
    }

    const items = [
      {
        label: 'Todas mis conversaciones',
        icon: component.state.filterStatusActive.includes(FILTER_STATUS.ALL) ? 'fa-regular fa-square-check' : 'fa-regular fa-square',
        command: () => {
          if(component.state.filterStatusActive.includes(FILTER_STATUS.ALL)) {
            component.setState((state) => {
              state.filterStatusActive = state.filterStatusActive.filter(f => f !== FILTER_STATUS.ALL);
            })
          } else {
            component.setState((state) => {
              state.filterStatusActive = Object.values(FILTER_STATUS);
            })
          }
        }
      },
      {
        label: 'Con mensajes nuevos',
        icon: component.state.filterStatusActive.includes(FILTER_STATUS.NEW_MESSAGES) ? 'fa-regular fa-square-check' : 'fa-regular fa-square',
        command: () => {
          filterStatusesArray(FILTER_STATUS.NEW_MESSAGES);
        }
      },
      {
        label: 'Tiempo de respuesta excedido',
        icon: component.state.filterStatusActive.includes(FILTER_STATUS.TIME_ELAPSED) ? 'fa-regular fa-square-check' : 'fa-regular fa-square',
        command: () => {
          filterStatusesArray(FILTER_STATUS.TIME_ELAPSED);
        }
      },
      {
        label: 'Finalizado automáticamente',
        icon: component.state.filterStatusActive.includes(FILTER_STATUS.FINISHED) ? 'fa-regular fa-square-check' : 'fa-regular fa-square',
        command: () => {
          filterStatusesArray(FILTER_STATUS.FINISHED);
        }
      },
      {
        label: 'Reasignada',
        icon: component.state.filterStatusActive.includes(FILTER_STATUS.REASSIGNED) ? 'fa-regular fa-square-check' : 'fa-regular fa-square',
        command: () => {
          filterStatusesArray(FILTER_STATUS.REASSIGNED);
        }
      }
    ];

    return <>
      <NeoSlideMenu ref={menu} model={items} popup className='FilterStatus'/>
      <NeoButtonMain type="button" icon="pi pi-filter" onClick={(event) => menu.current.toggle(event)} />
    </>
  }
  
  const FilterOrder = () => {
    const menu = useRef(null);
    const items = [
      {
        label: 'Cliente con más tiempo de espera',
        icon: component.state.filterOrderActive === FILTER_ORDER.LONGER_WAITING_TIME ? 'pi pi-circle-on' : 'pi pi-circle-off',
        command: () => {
          component.setState((state) => {
            state.filterOrderActive = FILTER_ORDER.LONGER_WAITING_TIME;
          })
        }
      },
      {
        label: 'Mensaje más reciente',
        icon: component.state.filterOrderActive === FILTER_ORDER.MORE_RECENT ? 'pi pi-circle-on' : 'pi pi-circle-off',
        command: () => {
          component.setState((state) => {
            state.filterOrderActive = FILTER_ORDER.MORE_RECENT;
          })
        }
      }
    ];

    return <>
      <NeoSlideMenu ref={menu} model={items} popup className='FilterOrder'/>
      <NeoButtonMain type="button" icon="pi pi-sort-amount-down" onClick={(event) => menu.current.toggle(event)} />
    </>
  }

  return (
    <div className={`ConversationsSection ${component.state.transition}`}>
      <ConversationsHeader
        isLoading={props.loading}
        line={chat.selected.line}
        agent={chat.selected.agent}
        filters={[FilterStatus, FilterOrder]}
      />

      <ContactConversationsList
        render={props?.render?.ContactConversationsList}
        isLoading={props.loading}
        conversations={filteredConversations}
        conversationSelected={chat.selected.conversation}
        onConversationSelect={(conversation) => {
          chat.dispatch((TYPE) => {
            return {
              type: TYPE.CONVERSATION.SELECTED.TYPE,
              payload: conversation.id
            }
          })
          if(conversation?.isReassigned) {
            if(authentication.user.role === 'AGENT') {
              if(conversation?.isReassigned) {
                chat.closeConversation();
              }
            }
          }
        }}
      />
    </div>
  );
}